import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { createSelector, createStructuredSelector } from 'reselect'

import { useLegalTextsReturn, LegalTextsBody } from '../../model/legal.model'
import { fetchLegalTexts } from '../../store/legalTexts/legalTexts.reducer'
import {
  selectLegalTexts,
  selectIsLegalLoading,
  selectLegalTextsError,
} from '../../store/legalTexts/legalTexts.selectors'

import { SignUpType } from '~/basket/model/lines/signup.model'
import { RootState } from '~/core/store/root.reducer'
import { selectCustomerValues, selectDocType } from '~/customer/store/customer.selectors'
import { getLines } from '~/line/store/line.selectors'
import { selectLineType } from '~/sale/store/sale.selectors'
import { getTariffs } from '~/tariff/store/tariff.selectors'
import { selectTheme } from '~/ui/theme/store/theme.selectors'

const selectCustomerLegal = createSelector(
  selectCustomerValues,
  selectDocType,
  ({ name, firstName, lastName, docId }, docType) => {
    return {
      name,
      firstName,
      lastName,
      docId,
      docType: {
        value: docType,
        id: 1,
      },
    }
  },
)

const selectBasketLegal = createSelector(getLines, getTariffs, (lines, tariffs) => {
  const linesKeys = Object.keys(lines)
  const orders = linesKeys.map((elem) => {
    const lineNumber =
      lines[elem].values.signUp.signUpType === SignUpType.NEW_LINE
        ? lines[elem].newNumber.data?.phoneNumber
        : lines[elem].values.signUp.lineNumber

    const tariffId = tariffs[elem].selectedTariffId
    const fullTariff = tariffs[elem].tariffList.data.find((tariff) => tariff.id === tariffId)

    const call = fullTariff?.call.map(({ callType, amount }) => ({ callType, amount }))

    return {
      signUp: {
        lineNumber,
      },
      tariff: {
        name: fullTariff?.name,
        mobileData: {
          amount: fullTariff?.mobileData?.amount,
        },
        call,
        price: fullTariff?.price,
      },
    }
  })

  return orders
})

const selectLegalTextsBody = createStructuredSelector<RootState, LegalTextsBody>({
  customer: selectCustomerLegal,
  basket: selectBasketLegal,
})

export default function useLegalTexts(): useLegalTextsReturn {
  const dispatch = useDispatch()

  const lineType = useSelector(selectLineType)

  const legalTexts = useSelector(selectLegalTexts)

  const brand = useSelector(selectTheme)

  const isLoading = useSelector(selectIsLegalLoading)

  const legalTextsBody = useSelector(selectLegalTextsBody)

  const legalTextsError = useSelector(selectLegalTextsError)

  const retryLegalTexts = useCallback(() => {
    dispatch(fetchLegalTexts({ body: legalTextsBody, saleType: lineType || '', tenant: brand }))
  }, [legalTextsBody])

  useEffect(() => {
    dispatch(fetchLegalTexts({ body: legalTextsBody, saleType: lineType || '', tenant: brand }))
  }, [])

  return { legalTexts, isLoading, legalTextsError, retryLegalTexts }
}
